import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fmtDateTimeCST } from '../../utils/datetime';
import {
  ArrowPathIcon,
  ClipboardDocumentIcon,
  DocumentArrowUpIcon,
  FunnelIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { screeningApi } from '../../services/api';
import {
  BID_FIT_LABELS,
  DECISION_CLASSES,
  PRESCREEN_CLASSES,
  PRESCREEN_LABELS,
  SOURCE_TYPE_LABELS,
  pickErrorMessage,
  type ScreeningDecision,
  type ScreeningOpportunity,
  type ScreeningPrescreenStatus,
  type ScreeningStats,
  type FetchPreviewResult,
} from '../../types/screening';

interface OpportunityListProps {
  onOpenDetail: (id: number) => void;
}

type InputTab = 'url' | 'batch' | 'file' | 'text';

const TABS: Array<{ key: InputTab; label: string }> = [
  { key: 'url', label: '网址抓取' },
  { key: 'batch', label: '批量导入网址' },
  { key: 'file', label: '上传公告文件' },
  { key: 'text', label: '粘贴文本' },
];

const PRESCREEN_FILTERS: Array<{ key: ScreeningPrescreenStatus | 'all'; label: string }> = [
  { key: 'all', label: '全部' },
  { key: 'keep', label: '建议细评' },
  { key: 'unsure', label: '待确认' },
  { key: 'drop', label: '已排除' },
  { key: 'none', label: '未初筛' },
];

const formatDeadline = (value: string | null): string => {
  if (!value) return '未提供';
  return fmtDateTimeCST(value);
  };

/** 招标信息列表：录入（网址/文件/文本）+ 列表 + 决策标签 */
export default function OpportunityList({ onOpenDetail }: OpportunityListProps) {
  const [items, setItems] = useState<ScreeningOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [decisionFilter, setDecisionFilter] = useState<ScreeningDecision | 'all'>('all');

  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState<InputTab>('url');
  const [urlInput, setUrlInput] = useState('');
  const [projectName, setProjectName] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedText, setUploadedText] = useState('');
  const [preview, setPreview] = useState<FetchPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [batchUrls, setBatchUrls] = useState('');
  const [batchImporting, setBatchImporting] = useState(false);
  const [batchSummary, setBatchSummary] = useState<string | null>(null);
  const [prescreenFilter, setPrescreenFilter] = useState<ScreeningPrescreenStatus | 'all'>('all');
  const [prescreening, setPrescreening] = useState(false);
  const [prescreenMessage, setPrescreenMessage] = useState<string | null>(null);
  const [reviewStats, setReviewStats] = useState<ScreeningStats | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const stats = useMemo(() => {
    const result: Record<ScreeningDecision, number> = {
      undecided: 0,
      go: 0,
      cautious: 0,
      no_go: 0,
    };
    items.forEach((item) => {
      result[item.decision] = (result[item.decision] || 0) + 1;
    });
    return result;
  }, [items]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await screeningApi.list({
        decision: decisionFilter === 'all' ? undefined : decisionFilter,
        prescreen: prescreenFilter === 'all' ? undefined : prescreenFilter,
        keyword: keyword.trim() || undefined,
      });
      setItems(res.data.items || []);
    } catch (err) {
      setError(pickErrorMessage(err, '加载招标信息列表失败'));
    } finally {
      setLoading(false);
    }
  }, [decisionFilter, prescreenFilter, keyword]);

  const loadReviewStats = useCallback(async () => {
    try {
      const res = await screeningApi.stats();
      setReviewStats(res.data.data);
    } catch {
      // 复盘统计失败不影响主流程，静默忽略
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    void loadReviewStats();
  }, [loadReviewStats]);

  const pendingPrescreenIds = useMemo(
    () => items.filter((item) => item.prescreen_status === 'none').map((item) => item.id),
    [items],
  );

  const resetCreateForm = () => {
    setUrlInput('');
    setProjectName('');
    setPastedText('');
    setUploadedFileName('');
    setUploadedText('');
    setBatchUrls('');
    setBatchSummary(null);
    setPreview(null);
    setLogs([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFetchPreview = async () => {
    if (!urlInput.trim()) {
      setError('请先填写公告网址');
      return;
    }
    setPreviewLoading(true);
    setError(null);
    try {
      const res = await screeningApi.fetchPreview(urlInput.trim());
      setPreview(res.data.data);
      if (!res.data.success) {
        setError(res.data.data?.error || '抓取失败，可改用粘贴文本');
      }
    } catch (err) {
      setError(pickErrorMessage(err, '抓取失败，可改用粘贴文本'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileChange = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    setError(null);
    setLogs([`正在读取文件：${file.name}`]);
    try {
      const res = await screeningApi.extractFile(file);
      setUploadedText(res.data.data.text);
      setUploadedFileName(res.data.data.file_name);
      setLogs([`已提取文本 ${res.data.data.char_count} 字，可开始解析`]);
    } catch (err) {
      setUploadedText('');
      setUploadedFileName('');
      setLogs([]);
      setError(pickErrorMessage(err, '文件解析失败，请改用粘贴文本'));
    }
  };

  const handleCreateAndParse = async () => {
    setError(null);

    let payload: Parameters<typeof screeningApi.create>[0];
    if (tab === 'url') {
      if (!preview?.char_count) {
        setError('请先点击「抓取预览」确认能取到公告正文');
        return;
      }
      payload = {
        name: projectName.trim() || preview.title || urlInput.trim(),
        source_type: 'url',
        source_url: urlInput.trim(),
      };
    } else if (tab === 'file') {
      if (!uploadedText.trim()) {
        setError('请先上传公告文件并完成文本提取');
        return;
      }
      payload = {
        name: projectName.trim() || uploadedFileName.replace(/\.[^.]+$/, ''),
        source_type: 'file',
        source_file_name: uploadedFileName,
        raw_text: uploadedText,
      };
    } else {
      if (pastedText.trim().length < 50) {
        setError('请粘贴完整的公告正文（至少 50 字）');
        return;
      }
      payload = {
        name: projectName.trim() || '未命名招标项目',
        source_type: 'text',
        raw_text: pastedText.trim(),
      };
    }

    setSubmitting(true);
    setLogs(['正在创建招标信息…']);
    try {
      const created = await screeningApi.create(payload);
      const opportunityId = created.data.data.id;
      setLogs((prev) => [...prev, '正在解析公告要点…']);

      await screeningApi.ingestStream(opportunityId, (event) => {
        if (event.error) {
          throw new Error(event.message || '要点抽取失败');
        }
        if (event.type === 'progress' && event.message) {
          setLogs((prev) => [...prev, event.message as string]);
        }
        if (event.type === 'result') {
          setLogs((prev) => [
            ...prev,
            `已识别 ${event.requirements_count ?? 0} 条要求`,
          ]);
        }
      });

      resetCreateForm();
      setShowCreate(false);
      await loadList();
      onOpenDetail(opportunityId);
    } catch (err) {
      setError(pickErrorMessage(err, '创建或解析失败，请重试'));
      setLogs((prev) => [...prev, pickErrorMessage(err, '创建或解析失败')]);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item: ScreeningOpportunity) => {
    if (!window.confirm(`确定删除「${item.name}」及其评估记录吗？`)) {
      return;
    }
    try {
      await screeningApi.remove(item.id);
      await loadList();
      await loadReviewStats();
    } catch (err) {
      setError(pickErrorMessage(err, '删除失败'));
    }
  };

  const handleBatchImport = async () => {
    const lines = batchUrls
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      setError('请粘贴公告网址，每行一个');
      return;
    }
    setBatchImporting(true);
    setError(null);
    setBatchSummary(null);
    try {
      const res = await screeningApi.batchImport({ urls: lines });
      setBatchSummary(res.data.message);
      setLogs(
        (res.data.skipped || [])
          .slice(0, 5)
          .map((item) => `跳过 ${item.url}：${item.reason}`),
      );
      setBatchUrls('');
      await loadList();
    } catch (err) {
      setError(pickErrorMessage(err, '批量导入失败，请检查链接后重试'));
    } finally {
      setBatchImporting(false);
    }
  };

  const runPrescreen = async (ids: number[]) => {
    if (ids.length === 0) {
      setError('没有需要初筛的招标信息');
      return;
    }
    setPrescreening(true);
    setError(null);
    setPrescreenMessage(null);
    setLogs([`开始批量初筛，共 ${ids.length} 条（抓取正文 → 匹配度初筛）…`]);
    try {
      await screeningApi.batchPrescreenStream(ids, (event) => {
        if (event.error) {
          throw new Error(event.message || '批量初筛失败');
        }
        if (event.type === 'progress' && event.message) {
          setLogs((prev) => [...prev, event.message as string]);
        }
        if (event.type === 'result') {
          setPrescreenMessage(event.message ?? null);
          const counts = event.counts || {};
          setLogs((prev) => [
            ...prev,
            `初筛结论：建议细评 ${counts.keep ?? 0} · 待确认 ${counts.unsure ?? 0} · 已排除 ${counts.drop ?? 0}`,
          ]);
        }
      });
      await loadList();
      await loadReviewStats();
    } catch (err) {
      setError(pickErrorMessage(err, '批量初筛失败，请重试'));
      setLogs((prev) => [...prev, pickErrorMessage(err, '批量初筛失败')]);
    } finally {
      setPrescreening(false);
    }
  };

  const decisionCard = (key: ScreeningDecision | 'all', label: string, count: number) => (
    <button
      key={key}
      type="button"
      onClick={() => setDecisionFilter(key)}
      className={`rounded-xl border px-4 py-3 text-left transition ${
        decisionFilter === key
          ? 'border-indigo-300 bg-indigo-50'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900">{count}</p>
    </button>
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {decisionCard('all', '全部', items.length)}
        {decisionCard('go', '适合投标', stats.go)}
        {decisionCard('cautious', '谨慎投标', stats.cautious)}
        {decisionCard('no_go', '不适合投标', stats.no_go)}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500">初筛</span>
          {PRESCREEN_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setPrescreenFilter(option.key)}
              className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
                prescreenFilter === option.key
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {reviewStats && reviewStats.decided_samples > 0 && (
            <span className="text-xs text-gray-500">
              已记录结果 {reviewStats.decided_samples} 条
              {reviewStats.go_hit_rate !== null &&
                ` · 审核建议投标的中标率 ${Math.round(reviewStats.go_hit_rate * 100)}%`}
            </span>
          )}
          <button
            type="button"
            onClick={() => void runPrescreen(pendingPrescreenIds)}
            disabled={prescreening || pendingPrescreenIds.length === 0}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
          >
            {prescreening ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <FunnelIcon className="h-4 w-4" />
            )}
            批量初筛未处理
            {pendingPrescreenIds.length > 0 && `（${pendingPrescreenIds.length}）`}
          </button>
        </div>
      </div>

      {prescreenMessage && (
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          {prescreenMessage}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="按项目名称搜索"
              className="w-56 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-indigo-400"
            />
            <button
              type="button"
              onClick={() => void loadList()}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              刷新
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowCreate((prev) => !prev);
              setError(null);
            }}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4" />
            录入招标信息
          </button>
        </div>

        {showCreate && (
          <div className="space-y-4 border-b border-gray-100 bg-gray-50/60 px-4 py-4">
            <div className="flex gap-2">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setTab(item.key);
                    setError(null);
                  }}
                  className={`rounded-lg px-3 py-1.5 text-sm transition ${
                    tab === item.key
                      ? 'bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-200'
                      : 'text-gray-600 hover:bg-white/70'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <input
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
                placeholder="项目名称（留空则自动识别）"
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
              />
            </div>

            {tab === 'url' && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                    <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                    <input
                      value={urlInput}
                      onChange={(event) => setUrlInput(event.target.value)}
                      placeholder="https://... 招标公告页面地址"
                      className="w-full text-sm outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleFetchPreview()}
                    disabled={previewLoading}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {previewLoading ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowPathIcon className="h-4 w-4" />
                    )}
                    抓取预览
                  </button>
                </div>
                {preview && (
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <p className="text-sm font-medium text-gray-800">
                      {preview.title || '（未取到页面标题）'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      来源：{preview.site || '-'} · 正文 {preview.char_count} 字
                      {preview.attachment_count > 0 && ` · 含 ${preview.attachment_count} 个附件`}
                      {preview.truncated ? '（已截断）' : ''}
                    </p>
                    <p className="mt-2 max-h-24 overflow-hidden text-xs leading-relaxed text-gray-600">
                      {preview.preview}
                    </p>
                    {preview.attachments?.length > 0 && (
                      <div className="mt-2 border-t border-gray-100 pt-2">
                        <p className="text-xs text-gray-500">公告附件</p>
                        <ul className="mt-1 space-y-0.5">
                          {preview.attachments.map((item) => (
                            <li key={item.url} className="flex items-center gap-1 text-xs">
                              <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                              <span className="truncate text-gray-700">{item.name}</span>
                              {item.ok ? (
                                <span className="shrink-0 text-emerald-600">
                                  {item.char_count} 字
                                </span>
                              ) : (
                                <span className="shrink-0 text-amber-600">{item.error}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === 'batch' && (
              <div className="space-y-3">
                <textarea
                  value={batchUrls}
                  onChange={(event) => setBatchUrls(event.target.value)}
                  rows={6}
                  placeholder={'每行粘贴一个公告网址，例如：\nhttps://www.xxx.com/notice/1001.html\nhttps://www.xxx.com/notice/1002.html'}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs outline-none focus:border-indigo-400"
                />
                <p className="text-xs leading-relaxed text-gray-500">
                  单次最多 20 条，已存在的公告会自动跳过。导入后点「开始批量初筛」：系统逐条抓取正文（含公告附件），
                  对照企业资料给出「建议细评 / 待确认 / 已排除」，只需对建议细评的公告再跑完整评估，可显著省时省成本。
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleBatchImport()}
                    disabled={batchImporting}
                    className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {batchImporting && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                    导入
                  </button>
                  <button
                    type="button"
                    onClick={() => void runPrescreen(pendingPrescreenIds)}
                    disabled={prescreening || pendingPrescreenIds.length === 0}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                  >
                    {prescreening ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <FunnelIcon className="h-4 w-4" />
                    )}
                    开始批量初筛
                    {pendingPrescreenIds.length > 0 && `（${pendingPrescreenIds.length} 条）`}
                  </button>
                </div>
                {batchSummary && (
                  <p className="text-xs text-emerald-700">{batchSummary}</p>
                )}
              </div>
            )}

            {tab === 'file' && (
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt,.md"
                  onChange={(event) => void handleFileChange(event.target.files?.[0])}
                  className="w-full rounded-lg border border-dashed border-gray-300 bg-white px-3 py-4 text-sm text-gray-600"
                />
                {uploadedFileName && (
                  <p className="text-xs text-gray-600">
                    <DocumentArrowUpIcon className="mr-1 inline h-4 w-4 text-gray-400" />
                    已解析：{uploadedFileName}（{uploadedText.length} 字）
                  </p>
                )}
              </div>
            )}

            {tab === 'text' && (
              <textarea
                value={pastedText}
                onChange={(event) => setPastedText(event.target.value)}
                rows={8}
                placeholder="粘贴招标公告正文（建议包含资格条件、评分办法、时间与保证金等信息）"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
              />
            )}

            {logs.length > 0 && (
              <div className="max-h-28 overflow-y-auto rounded-lg bg-gray-900/90 px-3 py-2 font-mono text-xs leading-relaxed text-gray-100">
                {logs.map((log, index) => (
                  <p key={`${log}-${index}`}>{log}</p>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2">
              {tab !== 'batch' && (
                <button
                  type="button"
                  onClick={() => void handleCreateAndParse()}
                  disabled={submitting}
                  className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                  录入并解析要点
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowCreate(false);
                  resetCreateForm();
                  setError(null);
                }}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                取消
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="border-b border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {loading && items.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-gray-500">加载中…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
              还没有招标信息，点击右上角「录入招标信息」开始
            </div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex flex-wrap items-start justify-between gap-3 px-4 py-3 transition hover:bg-gray-50 ${
                item.prescreen_status === 'drop' ? 'opacity-60' : ''
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onOpenDetail(item.id)}
                    className="truncate text-sm font-medium text-gray-900 hover:text-indigo-600"
                  >
                    {item.name}
                  </button>
                  <span
                    title={
                      item.latest_comparison?.created_at
                        ? `最近审核 ${formatDeadline(item.latest_comparison.created_at)}`
                        : '尚未做过「资质对比与决策」审核'
                    }
                    className={`rounded-full border px-2 py-0.5 text-xs ${DECISION_CLASSES[item.decision]}`}
                  >
                    {BID_FIT_LABELS[item.decision]}
                  </span>
                  <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
                    {SOURCE_TYPE_LABELS[item.source_type]}
                    {item.source_site ? ` · ${item.source_site}` : ''}
                  </span>
                  {item.prescreen_status !== 'none' && (
                    <span
                      title={item.prescreen_reason || ''}
                      className={`rounded-full border px-2 py-0.5 text-xs ${PRESCREEN_CLASSES[item.prescreen_status]}`}
                    >
                      初筛：{PRESCREEN_LABELS[item.prescreen_status]}
                      {item.prescreen_score !== null ? ` ${item.prescreen_score}` : ''}
                    </span>
                  )}
                </div>
                <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span>招标人：{item.tenderer || '未提供'}</span>
                  <span>地区：{item.region || '未提供'}</span>
                  <span>预算：{item.budget || '未提供'}</span>
                  <span>投标截止：{formatDeadline(item.bid_deadline)}</span>
                  <span>要求：{item.requirement_count ?? 0} 条</span>
                  {(item.attachments?.filter((file) => file.ok).length ?? 0) > 0 && (
                    <span>
                      附件：{item.attachments?.filter((file) => file.ok).length ?? 0} 个
                    </span>
                  )}
                  {item.latest_comparison?.best_total_score !== null &&
                    item.latest_comparison?.best_total_score !== undefined && (
                      <span>
                        审核得分：{item.latest_comparison.best_total_score}
                        {item.latest_comparison.best_company_name
                          ? `（${item.latest_comparison.best_company_name}）`
                          : ''}
                      </span>
                    )}
                  {!item.latest_comparison && item.latest_evaluation && (
                    <span>历史得分：{item.latest_evaluation.total_score}</span>
                  )}
                </p>
                {item.prescreen_reason && (
                  <p className="mt-1 truncate text-xs text-gray-400" title={item.prescreen_reason}>
                    初筛依据：{item.prescreen_reason}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpenDetail(item.id)}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-white"
                >
                  <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  查看与评估
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(item)}
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
